import React, { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import HelpHint from "@/components/HelpHint";
import { useConfirm } from "@/components/ConfirmModal";

/**
 * Ventilated payroll row for a single official with inline extras / deductions management.
 *
 * Props:
 *   - row: { off, games, basePay, extras: [], deductions: [] }
 *   - net, exSum, dedSum
 *   - eventId
 *   - onChanged: () => void
 *   - extraCols: optional additional <td> elements rendered before the "Gérer" cell
 *   - extraColSpan: total number of cols for the expandable panel (default 7)
 */
export default function OfficialPayrollRow({ row, net, exSum, dedSum, eventId, onChanged, extraCols = null, extraColSpan = 7 }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ kind: "extra", label: "Essence", amount: "" });
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();

  const addLine = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) {
      toast.error("Montant invalide");
      return;
    }
    if (!form.label.trim()) {
      toast.error("Description requise");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/events/${eventId}/extras`, {
        official_id: row.off.id,
        kind: form.kind,
        label: form.label.trim(),
        amount: amt,
      });
      toast.success(form.kind === "extra" ? "Extra ajouté" : "Déduction ajoutée");
      setForm({ kind: form.kind, label: form.kind === "extra" ? "Essence" : "Commande vêtements", amount: "" });
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removeLine = async (x) => {
    const ok = await confirm.ask({
      title: x.kind === "extra" ? "Supprimer cet extra?" : "Supprimer cette déduction?",
      message: `${x.label} — ${Number(x.amount).toFixed(2)} $`,
      confirmLabel: "Supprimer",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/events/${eventId}/extras/${x.id}`);
      toast.success("Supprimé");
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const hasLines = row.extras.length > 0 || row.deductions.length > 0;

  return (
    <>
      {confirm.modal}
      <tr
        className={`border-b border-[var(--border)] ${row.games === 0 ? "bg-[#dbeafe]" : ""}`}
        data-testid={`official-row-${row.off.id}`}
      >
        <td className="py-2 px-2">
          <div className="flex items-center gap-2">
            <span className="font-bold">{row.off.first_name} {row.off.last_name}</span>
            {row.games === 0 && (
              <span
                className="text-[9px] uppercase tracking-widest font-semibold border border-black bg-white px-1.5 py-0.5"
                data-testid={`official-unassigned-badge-${row.off.id}`}
                title="Aucune partie assignée à cet officiel pour cet événement"
              >
                Non cédulé
              </span>
            )}
          </div>
          {row.off.email && <div className="font-mono text-[10px] text-[var(--muted)]">{row.off.email}</div>}
        </td>
        <td className="py-2 px-2 text-right tabular">{row.games}</td>
        <td className="py-2 px-2 text-right tabular">{row.basePay.toFixed(2)} $</td>
        <td className="py-2 px-2 text-right tabular">{exSum > 0 ? `+${exSum.toFixed(2)} $` : "—"}</td>
        <td className="py-2 px-2 text-right tabular">{dedSum > 0 ? `−${dedSum.toFixed(2)} $` : "—"}</td>
        <td className="py-2 px-2 text-right tabular font-bold">{net.toFixed(2)} $</td>
        {extraCols}
        <td className="py-2 px-2 text-right">
          <span className="inline-flex items-center gap-1">
            <button
              type="button"
              onClick={() => setOpen(!open)}
              className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] hover:text-black inline-flex items-center gap-1"
              data-testid={`toggle-extras-${row.off.id}`}
            >
              <Plus size={12} strokeWidth={2.5} /> {open ? "Fermer" : "Gérer"}
            </button>
            {!open && (
              <HelpHint
                title="Gérer extras / déductions"
                text="Ajoute des EXTRAS (frais d'essence, bonus de performance — additionnés à la paie ET facturés au client) ou des DÉDUCTIONS (commande de vêtements, avance — soustraites de la paie SEULEMENT, pas facturées au client)."
                testid={`hint-toggle-extras-${row.off.id}`}
                size={11}
              />
            )}
          </span>
        </td>
      </tr>
      {open && (
        <tr className="bg-[var(--surface-2)]" data-testid={`extras-panel-${row.off.id}`}>
          <td colSpan={extraColSpan} className="py-3 px-2">
            {hasLines && (
              <div className="mb-3 space-y-1">
                {row.extras.map((x) => (
                  <div key={x.id} className="flex items-center gap-2 py-1 border-b border-[var(--border)]">
                    <span className="badge-dark">Extra</span>
                    <span className="flex-1">{x.label}</span>
                    <span className="tabular font-bold">+{Number(x.amount).toFixed(2)} $</span>
                    <button
                      onClick={() => removeLine(x)}
                      className="text-[var(--muted)] hover:text-black"
                      title="Supprimer"
                      data-testid={`del-extra-${x.id}`}
                    >
                      <Trash2 size={12} strokeWidth={2.5} />
                    </button>
                  </div>
                ))}
                {row.deductions.map((x) => (
                  <div key={x.id} className="flex items-center gap-2 py-1 border-b border-[var(--border)]">
                    <span className="badge">Déduction</span>
                    <span className="flex-1">{x.label}</span>
                    <span className="tabular font-bold">−{Number(x.amount).toFixed(2)} $</span>
                    <button
                      onClick={() => removeLine(x)}
                      className="text-[var(--muted)] hover:text-black"
                      title="Supprimer"
                      data-testid={`del-deduction-${x.id}`}
                    >
                      <Trash2 size={12} strokeWidth={2.5} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
              <div className="md:col-span-3">
                <label className="label block mb-1">Type</label>
                <select
                  value={form.kind}
                  onChange={(e) => setForm({
                    ...form,
                    kind: e.target.value,
                    label: e.target.value === "extra" ? "Essence" : "Commande vêtements",
                  })}
                  className="input-box text-xs py-1.5"
                  data-testid={`form-kind-${row.off.id}`}
                >
                  <option value="extra">Extra (+ paie + facture)</option>
                  <option value="deduction">Déduction (paie seulement)</option>
                </select>
              </div>
              <div className="md:col-span-5">
                <label className="label block mb-1">Description</label>
                <input
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  className="input-box text-xs py-1.5"
                  placeholder={form.kind === "extra" ? "Ex: Essence" : "Ex: Commande vêtements"}
                  data-testid={`form-label-${row.off.id}`}
                />
              </div>
              <div className="md:col-span-2">
                <label className="label block mb-1">Montant ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00"
                  className="input-box text-xs py-1.5 text-right tabular"
                  data-testid={`form-amount-${row.off.id}`}
                />
              </div>
              <div className="md:col-span-2">
                <button
                  onClick={addLine}
                  disabled={saving}
                  className="btn-primary w-full justify-center"
                  data-testid={`form-add-${row.off.id}`}
                >
                  <Plus size={12} strokeWidth={2.5} /> Ajouter
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
